import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useCartCount } from '@/features/cart/hooks/useCartCount';

// Ticket Icon for cart
const TicketIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 transition-transform duration-300 group-hover:scale-110">
    <path d="M16 10a4 4 0 0 1-8 0"></path><path d="M3.103 6.034h17.794"></path><path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z"></path>
  </svg>
);

interface CartIconBadgeProps {
  /**
   * Visual variant to match the header style (reserved for future use)
   */
  variant?: 'main' | 'club' | 'event' | 'artist';
}

/**
 * Self-contained cart icon with badge
 * 
 * Features:
 * - Uses stale-while-revalidate via useCartCount hook
 * - Displays localStorage value immediately for fast render
 * - Verifies with API in background
 * - Shows animated badge when count > 0
 */
const CartIconBadge: React.FC<CartIconBadgeProps> = ({ variant: _variant = 'main' }) => {
  const { cartCount } = useCartCount();
  // Note: useCartCount already triggers fetch on mount when authenticated
  // No need for additional useEffect here

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
    },
  };

  return (
    <Link to="/carts">
      <motion.button
        className="relative group"
        variants={buttonVariants}
        whileHover="hover"
        whileTap="tap"
      >
        <TicketIcon />
        {cartCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-semibold min-w-[1rem] h-4 px-1 rounded-full flex items-center justify-center"
          >
            {cartCount > 99 ? '99+' : cartCount}
          </motion.span>
        )}
      </motion.button>
    </Link>
  );
};

export default CartIconBadge;
